import { useState, useEffect, useCallback } from "react";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../components/Toast";
import { t } from "../i18n";

/* ─────────────────────────────────────────
   CONFIG — ปรับ BASE_URL ให้ตรงกับ backend
───────────────────────────────────────── */
const BASE_URL = process.env.REACT_APP_API_URL || "";

const ROLE_LABEL_KEY = {
  admin:    null,          // "Admin" is the same in both languages
  staff:    "auStaff",
  customer: "auCustomer",
  user:     "auCustomer",  // legacy role name
};
function getRoleLabel(lang, role) {
  const key = ROLE_LABEL_KEY[role];
  return key ? t(lang, key) : (role === "admin" ? "Admin" : role);
}

const ROLE_BADGE = {
  admin:    { bg: "#fef9c3", color: "#854d0e" },
  staff:    { bg: "#dbeafe", color: "#1e40af" },
  customer: { bg: "#f3f4f6", color: "#374151" },
  user:     { bg: "#f3f4f6", color: "#374151" },
};

const PROVIDER_ICON = {
  google:   "G",
  facebook: "f",
  local:    "✉",
};

/* Initials + color from name */
function getInitials(name = "") {
  return name.split(" ").map((w) => w[0] || "").slice(0, 2).join("").toUpperCase() || "?";
}
const INIT_COLORS = [
  ["#dbeafe","#1e40af"],["#dcfce7","#166534"],["#fce7f3","#9d174d"],
  ["#f3e8ff","#6b21a8"],["#e0f2fe","#0369a1"],["#fef9c3","#854d0e"],
  ["#fee2e2","#991b1b"],["#d1fae5","#065f46"],
];
function getColor(id = "") {
  const n = [...String(id)].reduce((s, c) => s + c.charCodeAt(0), 0);
  return INIT_COLORS[n % INIT_COLORS.length];
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function AdminUsers() {
  const { lang } = useLanguage();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [filter, setFilter]         = useState("all");
  const [search, setSearch]         = useState("");
  const [modal, setModal]           = useState(null);   // { type:"role"|"del", user, newRole? }
  const [pendingRoles, setPendingRoles] = useState({});  // id -> selected role
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast]           = useState(null);   // { msg, ok }

  /* ── helpers ─────────────────────────────── */
  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const getToken = () =>
    localStorage.getItem("token") ||
    document.cookie.replace(/(?:^|.*;\s*)token\s*=\s*([^;]*).*$|^.*$/, "$1");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  /* ── fetch users ──────────────────────────── */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/admin/users`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setError(e.message || t(lang, "auLoadFail"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  /* ── derived list ─────────────────────────── */
  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchQ =
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.phonenumber || "").includes(q);
    if (!matchQ) return false;
    if (filter === "all")      return true;
    if (filter === "admin")    return u.role === "admin";
    if (filter === "staff")    return u.role === "staff";
    if (filter === "customer") return u.role === "customer" || u.role === "user";
    return true;
  });

  const counts = {
    all:      users.length,
    admin:    users.filter((u) => u.role === "admin").length,
    staff:    users.filter((u) => u.role === "staff").length,
    customer: users.filter((u) => u.role === "customer" || u.role === "user").length,
  };

  const getCurrentRole = (u) => pendingRoles[u._id] ?? u.role;

  /* ── open modals ──────────────────────────── */
  const openRoleModal = (u) => {
    const nr = pendingRoles[u._id] ?? u.role;
    if (nr === u.role) return; // ไม่มีการเปลี่ยนแปลง
    setModal({ type: "role", user: u, newRole: nr });
  };

  const openDelModal = (u) => setModal({ type: "del", user: u });

  /* ── confirm actions ──────────────────────── */
  const confirm = async () => {
    if (!modal || actionLoading) return;
    setActionLoading(true);
    try {
      if (modal.type === "role") {
        const res = await fetch(
          `${BASE_URL}/api/admin/users/${modal.user._id}/role`,
          {
            method: "PUT",
            credentials: "include",
            headers: authHeaders(),
            body: JSON.stringify({ role: modal.newRole }),
          }
        );
        const d = await res.json();
        if (!res.ok) throw new Error(d.message || t(lang, "auRoleChangeFail"));

        setUsers((prev) =>
          prev.map((u) =>
            u._id === modal.user._id ? { ...u, role: modal.newRole } : u
          )
        );
        setPendingRoles((prev) => {
          const n = { ...prev };
          delete n[modal.user._id];
          return n;
        });
        showToast(`${t(lang, "auConfirmRoleDesc")} "${modal.user.name}" ${t(lang, "auTo")} ${getRoleLabel(lang, modal.newRole)} ✓`);
      } else {
        const res = await fetch(
          `${BASE_URL}/api/admin/users/${modal.user._id}`,
          {
            method: "DELETE",
            credentials: "include",
            headers: authHeaders(),
          }
        );
        const d = await res.json();
        if (!res.ok) throw new Error(d.message || t(lang, "auDeleteFail"));

        setUsers((prev) => prev.filter((u) => u._id !== modal.user._id));
        showToast(`${t(lang, "auDeleteDesc")} "${modal.user.name}" ✓`);
      }
    } catch (e) {
      showToast(e.message || t(lang, "auActionError"), false);
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  };

  /* ── render ───────────────────────────────── */
  return (
    <>
    <NavbarAdmin />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@300;400;500;600;700&display=swap');

        .au-wrap  { background:#f5f5f7; min-height:100vh; font-family:'Noto Sans Thai',sans-serif; }
        .au-page  { padding:24px 28px; max-width:1100px; margin:0 auto; }

        /* ── card ── */
        .au-card  { background:#fff; border-radius:14px; padding:24px; box-shadow:0 1px 4px rgba(0,0,0,.07),0 4px 16px rgba(0,0,0,.04); }

        /* ── header ── */
        .au-hdr   { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; flex-wrap:wrap; gap:12px; }
        .au-title { font-size:17px; font-weight:700; color:#111; display:flex; align-items:center; gap:8px; }
        .au-badge-count { background:#f0f0f5; color:#555; font-size:11px; font-weight:600; padding:2px 9px; border-radius:20px; margin-left:4px; }

        /* ── search ── */
        .au-search-wrap { position:relative; }
        .au-search-wrap input {
          border:1px solid #e0e0e6; border-radius:9px; padding:8px 12px 8px 34px;
          font-size:13px; color:#111; width:220px; outline:none; font-family:'Noto Sans Thai',sans-serif;
          transition:border-color .15s, box-shadow .15s;
        }
        .au-search-wrap input:focus { border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.1); }
        .au-search-ico { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#aaa; pointer-events:none; }
        .au-search-clear {
          position:absolute; right:9px; top:50%; transform:translateY(-50%);
          background:none; border:none; cursor:pointer; color:#aaa; font-size:14px; padding:0; line-height:1;
        }
        .au-search-clear:hover { color:#555; }

        /* ── refresh btn ── */
        .au-btn-refresh {
          display:flex; align-items:center; gap:5px; padding:7px 13px;
          border-radius:9px; border:1px solid #e0e0e6; background:#fff;
          font-size:12px; font-weight:500; color:#555; cursor:pointer; font-family:'Noto Sans Thai',sans-serif;
          transition:all .15s;
        }
        .au-btn-refresh:hover { background:#f5f5f7; border-color:#c0c0cc; }
        .au-btn-refresh.spinning svg { animation:spin .7s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }

        /* ── pills ── */
        .au-pills { display:flex; gap:6px; margin-bottom:20px; flex-wrap:wrap; }
        .au-pill  {
          padding:5px 14px; border-radius:20px; font-size:12.5px; font-weight:500;
          border:1px solid #e5e7eb; background:#fff; color:#374151; cursor:pointer;
          transition:all .15s; display:flex; align-items:center; gap:5px;
          font-family:'Noto Sans Thai',sans-serif;
        }
        .au-pill:hover  { background:#f9fafb; border-color:#c0c0cc; }
        .au-pill.active { background:#111; color:#fff; border-color:#111; }
        .au-pill-n { font-size:10.5px; opacity:.7; }

        /* ── user card ── */
        .au-user-card {
          border:1px solid #ebebef; border-radius:11px; padding:14px 16px;
          display:flex; align-items:center; gap:14px; margin-bottom:9px;
          background:#fff; transition:border-color .15s, box-shadow .15s;
        }
        .au-user-card:hover { border-color:#d5d5dd; box-shadow:0 2px 8px rgba(0,0,0,.05); }
        .au-user-card:last-child { margin-bottom:0; }

        /* ── avatar ── */
        .au-avatar-wrap {
          width:52px; height:52px; border-radius:10px; flex-shrink:0;
          overflow:hidden; position:relative;
        }
        .au-avatar-wrap img { width:100%; height:100%; object-fit:cover; }
        .au-avatar-init {
          width:52px; height:52px; border-radius:10px; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          font-size:17px; font-weight:600; letter-spacing:-0.5px;
        }
        .au-provider-dot {
          position:absolute; bottom:-2px; right:-2px; width:18px; height:18px;
          border-radius:50%; border:2px solid #fff; background:#e5e7eb;
          display:flex; align-items:center; justify-content:center;
          font-size:8px; font-weight:700; color:#555;
        }

        /* ── info ── */
        .au-info    { flex:1; min-width:0; }
        .au-name    { font-size:13.5px; font-weight:600; color:#111; display:flex; align-items:center; gap:7px; flex-wrap:wrap; margin-bottom:2px; }
        .au-email   { font-size:12px; color:#6b7280; }
        .au-meta    { font-size:11px; color:#aaa; margin-top:3px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .au-meta-dot { color:#ddd; }

        /* ── role badge ── */
        .rbadge {
          display:inline-flex; align-items:center; gap:3px;
          font-size:11px; font-weight:600; padding:2px 9px; border-radius:10px;
        }

        /* ── actions ── */
        .au-actions { display:flex; align-items:center; gap:7px; flex-shrink:0; flex-wrap:wrap; justify-content:flex-end; }
        .au-role-sel {
          font-size:12px; padding:6px 28px 6px 10px; border-radius:8px;
          border:1px solid #e0e0e6; background:#fff; color:#111; cursor:pointer;
          outline:none; min-width:110px; font-family:'Noto Sans Thai',sans-serif;
          appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23888'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right 9px center;
          transition:border-color .15s;
        }
        .au-role-sel:focus { border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.1); }
        .au-role-sel.changed { border-color:#f59e0b; background-color:#fffbeb; }

        .au-btn {
          display:flex; align-items:center; gap:4px; padding:6px 14px; border-radius:20px;
          border:none; font-size:12px; font-weight:500; cursor:pointer; white-space:nowrap;
          font-family:'Noto Sans Thai',sans-serif; transition:all .15s;
        }
        .au-btn-edit  { background:#111; color:#fff; }
        .au-btn-edit:hover  { background:#333; }
        .au-btn-edit:disabled { background:#ccc; cursor:not-allowed; }
        .au-btn-del   { background:#fff; color:#ef4444; border:1px solid #fca5a5; }
        .au-btn-del:hover   { background:#fef2f2; border-color:#ef4444; }

        /* ── admin tag ── */
        .au-super-tag {
          font-size:11px; background:#fef9c3; color:#854d0e;
          padding:4px 13px; border-radius:20px; font-weight:600;
          letter-spacing:.3px; border:1px solid #fde68a;
        }

        /* ── empty / error / loading ── */
        .au-state { text-align:center; padding:48px 20px; color:#aaa; }
        .au-state-icon { font-size:36px; margin-bottom:10px; }
        .au-state-msg  { font-size:13px; }
        .au-state-sub  { font-size:12px; color:#bbb; margin-top:4px; }
        .au-err-msg    { font-size:12px; color:#ef4444; margin-top:6px; }

        /* ── skeleton loader ── */
        .au-skeleton {
          border:1px solid #ebebef; border-radius:11px; padding:14px 16px;
          display:flex; align-items:center; gap:14px; margin-bottom:9px;
        }
        .au-skel-av { width:52px; height:52px; border-radius:10px; background:#f0f0f5; flex-shrink:0; }
        .au-skel-lines { flex:1; }
        .au-skel-line { height:10px; border-radius:5px; background:#f0f0f5; margin-bottom:8px; }
        .au-skel-line.w60 { width:60%; }
        .au-skel-line.w40 { width:40%; }
        .au-skel-line.w30 { width:30%; }
        @keyframes shimmer {
          0%   { opacity:.6; }
          50%  { opacity:1; }
          100% { opacity:.6; }
        }
        .au-skeleton .au-skel-av,
        .au-skeleton .au-skel-line { animation:shimmer 1.4s ease-in-out infinite; }

        /* ── modal ── */
        .au-modal-bg {
          position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:999;
          display:flex; align-items:center; justify-content:center;
          animation:fadeIn .15s ease;
        }
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        .au-modal {
          background:#fff; border-radius:14px; padding:24px; width:340px;
          box-shadow:0 8px 40px rgba(0,0,0,.18);
          animation:slideUp .18s ease;
        }
        @keyframes slideUp { from { transform:translateY(10px); opacity:0 } to { transform:translateY(0); opacity:1 } }
        .au-modal-icon { width:44px; height:44px; border-radius:11px; display:flex; align-items:center; justify-content:center; margin-bottom:12px; font-size:20px; }
        .au-modal-icon.del  { background:#fef2f2; }
        .au-modal-icon.role { background:#eff6ff; }
        .au-modal-title { font-size:15px; font-weight:700; color:#111; margin-bottom:6px; }
        .au-modal-desc  { font-size:13px; color:#6b7280; margin-bottom:20px; line-height:1.6; }
        .au-modal-btns  { display:flex; gap:8px; justify-content:flex-end; }
        .au-mbtn-cancel {
          padding:7px 16px; border-radius:8px; border:1px solid #e5e7eb;
          background:#fff; color:#374151; font-size:13px; cursor:pointer;
          font-family:'Noto Sans Thai',sans-serif; transition:background .15s;
        }
        .au-mbtn-cancel:hover { background:#f9fafb; }
        .au-mbtn-confirm-safe   {
          padding:7px 16px; border-radius:8px; border:none;
          background:#111; color:#fff; font-size:13px; font-weight:600;
          cursor:pointer; font-family:'Noto Sans Thai',sans-serif;
          display:flex; align-items:center; gap:6px;
        }
        .au-mbtn-confirm-safe:hover { background:#333; }
        .au-mbtn-confirm-danger {
          padding:7px 16px; border-radius:8px; border:none;
          background:#ef4444; color:#fff; font-size:13px; font-weight:600;
          cursor:pointer; font-family:'Noto Sans Thai',sans-serif;
          display:flex; align-items:center; gap:6px;
        }
        .au-mbtn-confirm-danger:hover { background:#dc2626; }
        .au-mbtn-confirm-safe:disabled,
        .au-mbtn-confirm-danger:disabled { opacity:.6; cursor:not-allowed; }

        /* ── toast ── */
        .au-toast {
          position:fixed; top:76px; right:28px;
          padding:10px 20px; border-radius:10px; font-size:13px; font-weight:500;
          font-family:'Noto Sans Thai',sans-serif; z-index:1200; white-space:nowrap;
          box-shadow:0 4px 20px rgba(0,0,0,.15);
          animation:toastIn .2s ease, toastOut .3s ease 2.7s forwards;
        }
        .au-toast.ok  { background:#111; color:#fff; }
        .au-toast.err { background:#ef4444; color:#fff; }
        @keyframes toastIn  { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:none } }
        @keyframes toastOut { from { opacity:1 } to { opacity:0 } }

        /* ── responsive ── */
        @media (max-width: 640px) {
          .au-page  { padding:14px; }
          .au-actions { flex-direction:column; align-items:stretch; }
          .au-role-sel { min-width:unset; width:100%; }
          .au-search-wrap input { width:160px; }
        }
      `}</style>

      {/* ── Toast ─────────────────────────── */}
      <Toast show={!!toast} type={toast?.ok ? "success" : "error"} message={toast?.msg} lang={lang} />

      {/* ── Confirm Modal ─────────────────── */}
      {modal && (
        <div className="au-modal-bg" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="au-modal">
            <div className={`au-modal-icon ${modal.type === "del" ? "del" : "role"}`}>
              {modal.type === "del" ? "🗑️" : "🔑"}
            </div>
            <div className="au-modal-title">
              {modal.type === "role" ? t(lang, "auConfirmRole") : t(lang, "auConfirmDelete")}
            </div>
            <div className="au-modal-desc">
              {modal.type === "role"
                ? <>{t(lang, "auConfirmRoleDesc")} <strong>"{modal.user.name}"</strong> {t(lang, "auFrom")} {getRoleLabel(lang, modal.user.role)} {t(lang, "auTo")} <strong>{getRoleLabel(lang, modal.newRole)}</strong> {t(lang, "auConfirmQ")}</>
                : <>{t(lang, "auDeleteDesc")} <strong>"{modal.user.name}"</strong><br /><span style={{ color: "#ef4444", fontSize: 12 }}>{t(lang, "auDeleteWarn")}</span></>
              }
            </div>
            <div className="au-modal-btns">
              <button className="au-mbtn-cancel" onClick={() => setModal(null)} disabled={actionLoading}>
                {t(lang, "auCancel")}
              </button>
              {modal.type === "role"
                ? <button className="au-mbtn-confirm-safe" onClick={confirm} disabled={actionLoading}>
                    {actionLoading ? t(lang, "auSaving") : t(lang, "auConfirm")}
                  </button>
                : <button className="au-mbtn-confirm-danger" onClick={confirm} disabled={actionLoading}>
                    {actionLoading ? t(lang, "auDeleting") : t(lang, "auDeleteBtn")}
                  </button>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── Page ──────────────────────────── */}
      <div className="au-wrap">
        <div className="au-page">
          <div className="au-card">

            {/* Header */}
            <div className="au-hdr">
              <div className="au-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                {t(lang, "auTitle")}
                <span className="au-badge-count">{users.length} {t(lang, "auPerson")}</span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Refresh */}
                <button
                  className={`au-btn-refresh ${loading ? "spinning" : ""}`}
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {t(lang, "auRefresh")}
                </button>

                {/* Search */}
                <div className="au-search-wrap">
                  <svg className="au-search-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder={t(lang, "auSearch")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="au-search-clear" onClick={() => setSearch("")}>✕</button>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Pills */}
            <div className="au-pills">
              {[
                { key: "all",      label: t(lang, "auAll"),      n: counts.all },
                { key: "admin",    label: "Admin",                n: counts.admin },
                { key: "staff",    label: t(lang, "auStaff"),    n: counts.staff },
                { key: "customer", label: t(lang, "auCustomer"), n: counts.customer },
              ].map((p) => (
                <button
                  key={p.key}
                  className={`au-pill ${filter === p.key ? "active" : ""}`}
                  onClick={() => setFilter(p.key)}
                >
                  {p.label}
                  <span className="au-pill-n">{p.n}</span>
                </button>
              ))}
              {search && (
                <span style={{ fontSize: 12, color: "#888", marginLeft: 4, alignSelf: "center" }}>
                  {t(lang, "auResultCount")} {filtered.length} {t(lang, "auItems")}
                </span>
              )}
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 9, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#b91c1c" }}>{t(lang, "auLoadFail")}</div>
                  <div className="au-err-msg">{error}</div>
                </div>
                <button
                  onClick={fetchUsers}
                  style={{ marginLeft: "auto", fontSize: 12, color: "#b91c1c", background: "none", border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}
                >
                  {t(lang, "auRetry")}
                </button>
              </div>
            )}

            {/* Skeleton */}
            {loading && !error && (
              Array.from({ length: 5 }).map((_, i) => (
                <div className="au-skeleton" key={i}>
                  <div className="au-skel-av" />
                  <div className="au-skel-lines">
                    <div className="au-skel-line w60" />
                    <div className="au-skel-line w40" />
                    <div className="au-skel-line w30" />
                  </div>
                </div>
              ))
            )}

            {/* Empty */}
            {!loading && !error && filtered.length === 0 && (
              <div className="au-state">
                <div className="au-state-icon">🔍</div>
                <div className="au-state-msg">{t(lang, "auNoMembers")}</div>
                {search && (
                  <div className="au-state-sub"><button onClick={() => setSearch("")} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>{t(lang, "auClearSearch")}</button></div>
                )}
              </div>
            )}

            {/* User List */}
            {!loading && !error && filtered.map((u) => {
              const isAdmin       = u.role === "admin";
              const currentRole   = getCurrentRole(u);
              const roleChanged   = currentRole !== u.role;
              const [bg, tc]      = getColor(u._id);
              const initials      = getInitials(u.name);
              const badgeStyle    = ROLE_BADGE[u.role] || ROLE_BADGE.customer;

              return (
                <div className="au-user-card" key={u._id}>
                  {/* Avatar */}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    {u.avatar
                      ? (
                        <div className="au-avatar-wrap">
                          <img src={u.avatar} alt={u.name} onError={(e) => { e.target.style.display = "none"; }} />
                          <div className="au-provider-dot">{PROVIDER_ICON[u.provider] || "?"}</div>
                        </div>
                      )
                      : (
                        <div className="au-avatar-init" style={{ background: bg, color: tc }}>
                          {initials}
                        </div>
                      )
                    }
                  </div>

                  {/* Info */}
                  <div className="au-info">
                    <div className="au-name">
                      {u.name || u.email}
                      <span className="rbadge" style={{ background: badgeStyle.bg, color: badgeStyle.color }}>
                        {getRoleLabel(lang, u.role)}
                      </span>
                    </div>
                    <div className="au-email">{u.email}</div>
                    <div className="au-meta">
                      {u.joined && u.joined !== "—" && <span>{t(lang, "auJoined")} {u.joined}</span>}
                      {u.phonenumber && (
                        <><span className="au-meta-dot">•</span><span>{u.phonenumber}</span></>
                      )}
                      {u.provider && u.provider !== "local" && (
                        <><span className="au-meta-dot">•</span>
                        <span style={{ color: u.provider === "google" ? "#ea4335" : "#1877f2" }}>
                          {u.provider === "google" ? "Google" : "Facebook"}
                        </span></>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="au-actions">
                    {isAdmin ? (
                      <span className="au-super-tag">Super Admin</span>
                    ) : (
                      <>
                        <select
                          className={`au-role-sel ${roleChanged ? "changed" : ""}`}
                          value={currentRole}
                          onChange={(e) =>
                            setPendingRoles((prev) => ({ ...prev, [u._id]: e.target.value }))
                          }
                        >
                          <option value="staff">{t(lang, "auStaff")}</option>
                          <option value="user">{t(lang, "auCustomer")}</option>
                          <option value="test">Test</option>
                        </select>
                        <button
                          className="au-btn au-btn-edit"
                          onClick={() => openRoleModal(u)}
                          disabled={!roleChanged}
                          title={!roleChanged ? t(lang, "auSelectRoleFirst") : t(lang, "auSave")}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          {t(lang, "auSave")}
                        </button>
                        <button
                          className="au-btn au-btn-del"
                          onClick={() => openDelModal(u)}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                          </svg>
                          {t(lang, "auDelete")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </div>
    </>
  );
}